import { XCircle, type LucideIcon } from 'lucide-react';
import { formatCurrency } from '@/lib/format-currency';
import { NOTIFICATION_TYPE_META, type NotificationType } from './notification-types';

/** Visual tone of the detail pop-up (drives the spotlight header + eyebrow). */
export type DetailTone = 'success' | 'danger' | 'accent' | 'muted';

/** One quick-glance chip under the body, e.g. "3 of 10 contracts". */
export interface NotificationFact {
  key: string;
  label: string;
}

const MAX_FACTS = 4;

function metaNumber(meta: Record<string, unknown> | undefined, key: string): number | undefined {
  const value = meta?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function metaString(meta: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = meta?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The pop-up tone. Mostly the row icon's tone, with one correction the row
 * doesn't need: a LOST market resolution reads red, not the type's green
 * (`meta.won` distinguishes the two — the type is shared).
 */
export function detailTone(type: NotificationType, meta: Record<string, unknown> | undefined): DetailTone {
  if (type === 'MARKET_RESOLUTION' && meta?.won === false) return 'danger';
  return NOTIFICATION_TYPE_META[type]?.tone ?? 'muted';
}

/** The fallback medallion icon — same correction as {@link detailTone} for a lost market. */
export function detailIcon(type: NotificationType, meta: Record<string, unknown> | undefined): LucideIcon {
  if (type === 'MARKET_RESOLUTION' && meta?.won === false) return XCircle;
  return NOTIFICATION_TYPE_META[type]?.icon ?? XCircle;
}

/** Contract price stored in dollars (0–1) → the cents label players see. Values > 1 are already cents. */
function priceLabel(price: number): string {
  const cents = price <= 1 ? Math.round(price * 100) : Math.round(price);
  return `${cents}¢`;
}

/**
 * Quick-glance facts derived from the notification's `meta` payload — the
 * numbers a player would otherwise have to parse out of the body text
 * (payout, fill progress, price, position move, closing time). Producers only
 * set the fields relevant to their type, so most notifications yield 1–3
 * chips; unknown/absent fields simply don't chip. Capped at {@link MAX_FACTS}.
 */
export function notificationFacts(meta: Record<string, unknown> | undefined): NotificationFact[] {
  const facts: NotificationFact[] = [];

  const amount = metaNumber(meta, 'amount');
  if (amount !== undefined) {
    facts.push({ key: 'amount', label: formatCurrency(Math.round(amount * 100), metaString(meta, 'currency') ?? 'usd') });
  }

  const settlement = metaNumber(meta, 'settlementAmount');
  if (settlement !== undefined && settlement > 0) {
    facts.push({ key: 'payout', label: `Payout ${formatCurrency(Math.round(settlement * 100))}` });
  }

  const changePercent = metaNumber(meta, 'changePercent');
  if (changePercent !== undefined) {
    facts.push({ key: 'change', label: `${changePercent >= 0 ? '+' : '−'}${Math.round(Math.abs(changePercent))}%` });
  }

  const filledQuantity = metaNumber(meta, 'filledQuantity');
  const orderQuantity = metaNumber(meta, 'orderQuantity');
  if (filledQuantity !== undefined && orderQuantity !== undefined) {
    facts.push({ key: 'quantity', label: `${filledQuantity} of ${orderQuantity} contracts` });
  } else if (filledQuantity !== undefined) {
    facts.push({ key: 'quantity', label: `${filledQuantity} contracts` });
  } else {
    const filledPercent = metaNumber(meta, 'filledPercent');
    if (filledPercent !== undefined) {
      facts.push({ key: 'filled', label: `${Math.round(filledPercent)}% filled` });
    }
  }

  const avgPrice = metaNumber(meta, 'avgPrice');
  if (avgPrice !== undefined) {
    facts.push({ key: 'price', label: `${priceLabel(avgPrice)} avg` });
  }

  const closeTime = metaString(meta, 'closeTime');
  if (closeTime) {
    const date = new Date(closeTime);
    if (!Number.isNaN(date.getTime())) {
      facts.push({
        key: 'closes',
        label: `Closes ${date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`,
      });
    }
  }

  return facts.slice(0, MAX_FACTS);
}
